import React, { useState, useRef, useEffect } from 'react'
import {
  Box,
  IconButton,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Tooltip,
  Divider,
} from '@mui/material'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import CloseIcon from '@mui/icons-material/Close'
import SendIcon from '@mui/icons-material/Send'
import MinimizeIcon from '@mui/icons-material/Remove'
import { getToken } from '../../services/auth'
import { useSettings } from '../../contexts/SettingsContext'
import { useTranslation } from 'react-i18next'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api'

interface Message {
  role: 'user' | 'assistant'
  content: string
}

/**
 * Floating AI chat widget for the admin panel
 */
export default function ChatWidget(): React.JSX.Element {
  const { isRTL, language } = useSettings()
  const { t, i18n } = useTranslation()
  // When RTL: side menu is on the right → chat button on the left. When LTR: opposite.
  // MUI's RTL emotion cache (stylis-plugin-rtl, see App.tsx) mirrors `right` to `left`
  // in RTL, so a physical `right` here lands on the correct side in both directions.
  const side = 'right'
  const [open, setOpen] = useState(false)
  const [minimized, setMinimized] = useState(false)
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open && !minimized) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, open, minimized])

  useEffect(() => {
    setMessages([
      {
        role: 'assistant',
        content: t('chat.greeting'),
      },
    ])
    // Re-render the greeting whenever the active language changes (via Settings
    // or i18n directly), so it never lags behind the rest of the UI.
  }, [language, i18n.language, t])

  const sendMessage = async () => {
    const text = input.trim()
    if (!text || loading) return

    const userMsg: Message = { role: 'user', content: text }
    const newMessages = [...messages, userMsg]
    setMessages(newMessages)
    setInput('')
    setLoading(true)

    try {
      const token = getToken()
      // Send only user/assistant messages (not the initial greeting from us)
      const payload = newMessages.filter(m => !(m.role === 'assistant' && m === messages[0]))

      const res = await fetch(`${API_BASE_URL}/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ messages: payload, language }),
      })

      if (!res.ok) {
        throw new Error('Server error')
      }

      const data = await res.json()
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }])
    } catch {
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: t('chat.error'),
        },
      ])
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  if (!open) {
    return (
      <Tooltip title={t('chat.tooltip')} placement={isRTL ? 'right' : 'left'}>
        <IconButton
          onClick={() => setOpen(true)}
          aria-label={t('chat.tooltip')}
          sx={{
            position: 'fixed',
            bottom: 28,
            [side]: 28,
            zIndex: 1300,
            width: 56,
            height: 56,
            background: 'linear-gradient(135deg, #f5a623 0%, #f7c34a 100%)',
            color: '#fff',
            boxShadow: '0 4px 20px rgba(245,166,35,0.5)',
            '&:hover': {
              background: 'linear-gradient(135deg, #e09520 0%, #f5b930 100%)',
              transform: 'scale(1.08)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <SmartToyIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
    )
  }

  return (
    <Paper
      elevation={12}
      sx={{
        position: 'fixed',
        bottom: 28,
        [side]: 28,
        zIndex: 1300,
        width: 380,
        height: minimized ? 'auto' : 560,
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
        border: '1px solid rgba(245,166,35,0.25)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          background: 'linear-gradient(135deg, #f5a623 0%, #f7c34a 100%)',
          color: '#fff',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setMinimized(m => !m)}
      >
        <SmartToyIcon fontSize="small" />
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
          {t('chat.title')}
        </Typography>
        <IconButton
          size="small"
          onClick={e => { e.stopPropagation(); setMinimized(m => !m) }}
          aria-label={t('chat.minimize')}
          sx={{ color: '#fff', p: 0.5 }}
        >
          <MinimizeIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={e => { e.stopPropagation(); setOpen(false) }}
          aria-label={t('chat.close')}
          sx={{ color: '#fff', p: 0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {!minimized && (
        <>
          <Divider />

          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 2,
              py: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              bgcolor: '#fafafa',
            }}
          >
            {messages.map((msg, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <Box
                  sx={{
                    maxWidth: '82%',
                    px: 1.8,
                    py: 1,
                    borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    bgcolor: msg.role === 'user' ? '#f5a623' : '#fff',
                    color: msg.role === 'user' ? '#fff' : '#1a1a1a',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                    border: msg.role === 'assistant' ? '1px solid #eee' : 'none',
                    fontSize: 14,
                    lineHeight: 1.55,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {msg.content}
                </Box>
              </Box>
            ))}

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: '18px 18px 18px 4px',
                    bgcolor: '#fff',
                    border: '1px solid #eee',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <CircularProgress size={14} sx={{ color: '#f5a623' }} />
                  <Typography variant="caption" color="text.secondary">{t('chat.thinking')}</Typography>
                </Box>
              </Box>
            )}

            <div ref={bottomRef} />
          </Box>

          <Divider />

          {/* Input */}
          <Box sx={{ px: 2, py: 1.5, bgcolor: '#fff', display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              size="small"
              placeholder={t('chat.placeholder')}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  fontSize: 14,
                  '&.Mui-focused fieldset': { borderColor: '#f5a623' },
                },
              }}
            />
            <IconButton
              onClick={sendMessage}
              aria-label={t('chat.send')}
              disabled={!input.trim() || loading}
              sx={{
                bgcolor: '#f5a623',
                color: '#fff',
                width: 40,
                height: 40,
                borderRadius: 2,
                flexShrink: 0,
                '&:hover': { bgcolor: '#e09520' },
                '&:disabled': { bgcolor: '#ddd', color: '#aaa' },
              }}
            >
              <SendIcon fontSize="small" sx={{ transform: isRTL ? 'scaleX(-1)' : undefined }} />
            </IconButton>
          </Box>
        </>
      )}
    </Paper>
  )
}
